import React, { useState, useEffect } from "react";
import axios from "axios";
import { Edit, Trash2, Search, X, Save, Zap, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const AdminProducts = () => {
  const { user } = useAuth();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const adminToken = user?.accessToken || user?.token;
  const BASE_URL = "https://wethrift-backend.onrender.com/api/product";

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(BASE_URL);
      setAllProducts(res.data);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Delete this asset permanently?")) {
      try {
        await axios.delete(`${BASE_URL}/find/${id}`, {
          headers: { token: `Bearer ${adminToken}` }
        });
        setAllProducts(allProducts.filter(p => p._id !== id));
      } catch (err) {
        alert("Delete failed");
      }
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct({ ...product });
    setIsEditModalOpen(true);
  };

  const handleUpdateProduct = async () => {
    setBtnLoading(true);
    try {
      const res = await axios.put(`${BASE_URL}/find/${editingProduct._id}`, editingProduct, {
        headers: { token: `Bearer ${adminToken}` }
      });
      
      if (res.status === 200) {
        setIsEditModalOpen(false);
        fetchData();
        alert("Asset Updated Successfully");
      }
    } catch (err) {
      console.error("Update error", err.response);
      alert("Update failed: Check Console");
    } finally {
      setBtnLoading(false);
    }
  };

  const filteredProducts = allProducts.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-20 p-4 lg:p-10 bg-black min-h-screen text-white">
      
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 border-b border-white/[0.05] pb-8">
        <div>
          <h1 className="text-3xl lg:text-5xl font-black tracking-tighter italic">Asset_Inventory</h1>
          <p className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.4em] mt-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Terminal Active: {allProducts.length} SKU
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row w-full lg:w-auto gap-4">
            <div className="relative group flex-1 sm:w-80">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input 
                    type="text" 
                    placeholder="Search inventory..." 
                    className="w-full bg-zinc-950 border border-white/[0.08] pl-12 pr-4 py-4 rounded-2xl text-xs font-bold outline-none text-white focus:border-emerald-500/50"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-zinc-950/50 border border-white/[0.05] rounded-[2.5rem] overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 border-b border-white/[0.05] bg-zinc-900/30">
                <th className="p-8">Visual_Asset</th>
                <th className="p-8 text-center">Category</th>
                <th className="p-8">Pricing</th>
                <th className="p-8 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.03]">
              {loading ? (
                <tr><td colSpan="4" className="p-32 text-center animate-pulse text-zinc-600 font-black tracking-widest uppercase">Syncing Cloud...</td></tr>
              ) : filteredProducts.map((p) => (
                <tr key={p._id} className="hover:bg-white/[0.01] transition-all group">
                  <td className="p-8">
                    <div className="flex items-center gap-6">
                      <img src={Array.isArray(p.img) ? p.img[0] : p.img} className="w-20 h-24 rounded-2xl object-cover border border-white/10" alt="" crossOrigin="anonymous" />
                      <div>
                        <p className="text-lg font-black text-white italic tracking-tighter uppercase">{p.name}</p>
                        <p className="text-[10px] text-zinc-500 font-black uppercase tracking-widest mt-1">ID: {p._id.slice(-6)}</p>
                      </div>
                    </div>
                  </td>
                  
                  <td className="p-8 text-center">
                    <span className="text-[10px] bg-white/5 border border-white/10 px-4 py-2 rounded-full font-black uppercase tracking-widest text-emerald-500">
                      {p.category}
                    </span>
                  </td>

                  <td className="p-8 text-2xl font-black italic tracking-tighter">₹{p.price}</td>

                  <td className="p-8 text-right">
                    <div className="flex justify-end gap-3 opacity-20 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEditClick(p)} className="p-4 bg-zinc-900 text-white rounded-2xl hover:bg-emerald-500 hover:text-black border border-white/5 transition-all"><Edit size={18} /></button>
                      <button onClick={() => handleDelete(p._id)} className="p-4 bg-zinc-900 text-white rounded-2xl hover:bg-red-600 border border-white/5 transition-all"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* EDIT MODAL */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 overflow-y-auto">
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setIsEditModalOpen(false)}></div>
            <div className="relative bg-zinc-950 border border-emerald-500/20 w-full max-w-2xl rounded-[2.5rem] p-10 animate-in zoom-in-95 duration-300">
                <h2 className="text-2xl font-black italic text-white mb-8 uppercase tracking-tighter flex items-center gap-2"><Zap className="text-emerald-500" fill="currentColor"/> Modify_Asset</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Product Name</label>
                        <input className="w-full bg-black border border-white/10 p-4 rounded-xl text-white font-bold outline-none focus:border-emerald-500" value={editingProduct.name} onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Price (₹)</label>
                        <input type="number" className="w-full bg-black border border-white/10 p-4 rounded-xl text-white font-bold outline-none focus:border-emerald-500" value={editingProduct.price} onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Category</label>
                        <input className="w-full bg-black border border-white/10 p-4 rounded-xl text-white font-bold outline-none focus:border-emerald-500" value={editingProduct.category} onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})} />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Description</label>
                        <textarea rows="3" className="w-full bg-black border border-white/10 p-4 rounded-xl text-zinc-400 text-xs font-medium outline-none focus:border-emerald-500" value={editingProduct.description} onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})} />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Images (One URL per line)</label>
                        <textarea rows="3" className="w-full bg-black border border-white/10 p-4 rounded-xl text-zinc-400 text-xs font-mono outline-none focus:border-emerald-500" 
                        value={Array.isArray(editingProduct.img) ? editingProduct.img.join("\n") : editingProduct.img} 
                        onChange={(e) => setEditingProduct({...editingProduct, img: e.target.value.split("\n")})} />
                    </div>
                </div>
                <div className="mt-10 flex gap-4">
                    <button onClick={handleUpdateProduct} disabled={btnLoading} className="flex-1 bg-emerald-500 text-black py-4 rounded-xl font-black uppercase text-xs flex items-center justify-center gap-2 hover:bg-white transition-all">
                        {btnLoading ? <Loader2 className="animate-spin" size={16}/> : <><Save size={16} /> Save Changes</>}
                    </button>
                    <button onClick={() => setIsEditModalOpen(false)} className="px-8 bg-zinc-900 text-zinc-500 py-4 rounded-xl font-black uppercase text-xs">Cancel</button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;